import React, { useContext, useState } from 'react';
import Header from '../components/common/MainContentHeader';
import { UserContext } from '../context/UserContext';
import BlankDashboard from '../components/dashboard/BlankDashboard';
import useI18n from '../hooks/useI18n';
import DefaultDashboard from '../components/dashboard/DefaultDashboard';
import AdminDashboard from '../components/dashboard/AdminDashboard';
import { SortLinksByOptions } from '../types/enums';
import Loading from '../components/common/Loading';
import BrandNoCampaign from '@/components/dashboard/BrandNoCampaign';
import CampaignSection from '@/components/dashboard/CampaignSection';
import CampaignRefreshControls from '@/components/dashboard/campaign/CampaignRefreshControls';
import { CampaignSectionSkeleton } from '@/components/dashboard/campaign/CampaignSkeletons';
import { ILink } from '@/interfaces/Link';
import { useGetCampaignStatsQuery } from '../hooks/queries/useCampaignQueries';
import AppDownloadComponent from '@/components/AppDownloadComponent';
import { useUserLinksQuery } from '../hooks/queries/useLinkQueries';

const Dashboard: React.FC = () => {
  const { t } = useI18n();

  const userContext = useContext(UserContext);
  const [selectedTab, setSelectedTab] = useState(0);
  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }
  const { user, isDummyUser } = userContext;
  const isBrandUser = user?.role === 'brandUser';

  const tabs = [
    {
      label: t('dashboard.tabs.newlyAdded'),
      value: SortLinksByOptions.NewlyAdded,
    },
    { label: t('dashboard.tabs.topLinks'), value: SortLinksByOptions.TopLinks },
    { label: t('dashboard.tabs.oldLinks'), value: SortLinksByOptions.OldLinks },
    {
      label: t('dashboard.tabs.favourites'),
      value: SortLinksByOptions.Favourites,
    },
  ];

  // Smart links are a creator-only feature — brands never render a link list,
  // so the query stays disabled for them rather than blocking their dashboard.
  const {
    data: linksData,
    isLoading,
    refetch: refetchLinks,
  } = useUserLinksQuery(
    user?._id || '',
    tabs[selectedTab].value,
    1,
    3,
    !isBrandUser,
  );
  const userLinks: ILink[] = linksData?.links || [];

  const brandId =
    isBrandUser && user?.brand
      ? typeof user.brand === 'object'
        ? user.brand._id
        : user.brand
      : '';
  const { data: campaignStats, isLoading: isStatsLoading } =
    useGetCampaignStatsQuery(brandId, !!brandId);

  // A failed stats fetch resolves to null — treat that as "no campaign" and
  // show the empty state rather than blanking the dashboard.
  const hasActiveCampaign =
    !!campaignStats && (campaignStats.live > 0 || campaignStats.paused > 0);

  const getTitle = () => {
    if (user && user.firstName) {
      return `${user.firstName}'s`;
    }
    return '';
  };

  return (
    <>
      {user && user.role === 'admin' ? (
        <AdminDashboard />
      ) : (
        <div
          data-testid={isBrandUser ? 'brand-dashboard' : 'creator-dashboard'}
          // Brands run on a white page body; the header band below keeps its
          // own #F0F5FF background, so only the space under the panel changes.
          className={`${isBrandUser ? 'bg-white' : 'bg-[#F0F5FF]'} h-[calc(100%-152px)] h-full`}
        >
          {user?.role === 'user' && <AppDownloadComponent />}
          <div
            className={
              isBrandUser
                ? // Figma header band: 80px tall, 24px gutters, text centered.
                  'flex min-h-[80px] items-center justify-between gap-4 px-[24px] py-3 bg-white lg:bg-[#F0F5FF]'
                : `p-[24px] lg:bg-[#F0F5FF] bg-white ${userLinks.length > 0 ? 'pb-[24px]' : 'pb-0'}`
            }
          >
            {/* The brand dashboard header is the bare "Dashboard" label. */}
            <Header
              title={isBrandUser ? '' : getTitle()}
              subtitle={String(t('common.dashboard'))}
            />
            {/* The refresh controls only mean anything once data can exist. */}
            {isBrandUser && hasActiveCampaign && (
              <CampaignRefreshControls
                brandId={brandId}
                enabled={!isDummyUser}
              />
            )}
          </div>

          {/* Brands wait on campaign stats too, or a live campaign briefly
              renders as the no-campaign empty state. They see the campaign
              skeleton meanwhile; creators keep the spinner. */}
          {isBrandUser && isStatsLoading ? (
            <div className="bg-white py-6">
              <CampaignSectionSkeleton />
            </div>
          ) : isLoading ? (
            <Loading centeredToParent={true} />
          ) : (
            <>
              {isBrandUser ? (
                hasActiveCampaign ? (
                  <div className="bg-white py-6">
                    <CampaignSection
                      campaignStatus={
                        campaignStats.live > 0 ? 'live' : 'paused'
                      }
                    />
                  </div>
                ) : (
                  <div className="bg-white py-6">
                    <BrandNoCampaign />
                  </div>
                )
              ) : userLinks.length > 0 ? (
                <DefaultDashboard
                  links={userLinks}
                  tabs={tabs}
                  selectedTab={selectedTab}
                  setSelectedTab={setSelectedTab}
                  refetchLinks={async () => {
                    await refetchLinks();
                  }}
                />
              ) : (
                <div className="bg-[#F0F5FF] p-6">
                  <BlankDashboard />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default Dashboard;
